import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Column, Table } from "@tanstack/react-table";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";

import type { FilterOption, GroupedFilterOptions } from "./types";

interface DataTableSelectedFiltersProps<TData> {
  table: Table<TData>;
}

interface DataTableFilterBadgeProps<TData, TValue> {
  column: Column<TData, TValue>;
  table: Table<TData>;
}

export function DataTableSelectedFilters<TData>({ table }: DataTableSelectedFiltersProps<TData>) {
  const { columnFilters } = table.getState();
  const mappedColumnFilters = columnFilters
    .map((f) => ({
      id: f.id,
      column: table.getColumn(f.id),
    }))
    .filter((f): f is { id: string; column: Column<TData> } => f.column !== undefined);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {mappedColumnFilters.map((f) => (
        <DataTableFilterBadge key={f.id} column={f.column} table={table} />
      ))}
    </div>
  );
}

function DataTableFilterBadge<TData, TValue>({ column, table }: DataTableFilterBadgeProps<TData, TValue>) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { data: rawOptions, isLoading } = useQuery({
    queryKey: [column.id, "data-table-filters", column, table],
    queryFn: () => column.columnDef.meta?.getColumnFilters?.({ column, table }) ?? [],
    staleTime: 1000 * 60 * 10,
    placeholderData: keepPreviousData,
  });
  const selectedValues = new Set((column.getFilterValue() as string[] | undefined | null) ?? []);

  // Separate grouped and flat options
  const groupedOptions: GroupedFilterOptions[] = [];
  const flatOptions: FilterOption[] = [];

  if (rawOptions && Array.isArray(rawOptions)) {
    rawOptions.forEach((item) => {
      if ("groupLabel" in item && "options" in item) {
        groupedOptions.push(item);
      } else if ("label" in item && "value" in item) {
        flatOptions.push(item);
      }
    });
  }

  // Create a flat map of all options for label lookup
  const allOptionsMap = new Map<string, FilterOption>();
  flatOptions.forEach((option) => {
    allOptionsMap.set(option.value, option);
  });
  groupedOptions.forEach((group) => {
    group.options.forEach((option) => {
      allOptionsMap.set(option.value, option);
    });
  });

  if (isLoading || allOptionsMap.size === 0) {
    return <Skeleton className="h-6 w-32 animate-pulse bg-white/80" />;
  }

  const handleToggleGroup = (group: GroupedFilterOptions) => {
    const groupValues = group.options.map((opt) => opt.value);
    const newSelectedValues = new Set(selectedValues);

    const allSelected = groupValues.every((value) => selectedValues.has(value));

    if (allSelected) {
      groupValues.forEach((value) => {
        newSelectedValues.delete(value);
      });
    } else {
      groupValues.forEach((value) => {
        newSelectedValues.add(value);
      });
    }

    const filterValues = Array.from(newSelectedValues);
    column.setFilterValue(filterValues.length ? filterValues : undefined);
  };

  const toggleGroupExpansion = (groupLabel: string) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (expandedGroups.has(groupLabel)) {
      newExpandedGroups.delete(groupLabel);
    } else {
      newExpandedGroups.add(groupLabel);
    }
    setExpandedGroups(newExpandedGroups);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            "h-6 cursor-pointer bg-background p-0",
            "data-[state=open]:border-[#8c72e2] data-[state=open]:ring-[3px] data-[state=open]:ring-[rgba(133,86,233,0.2)]",
          )}
        >
          <div className="space-x-1 pr-1 pl-2">
            <span className="capitalize">{column.columnDef.meta?.displayName ?? column.id}:</span>
            <span>
              {selectedValues.size > 2
                ? `${selectedValues.size} selected`
                : Array.from(selectedValues)
                    .map((value) => allOptionsMap.get(value)?.label ?? value)
                    .join(", ")}
            </span>
          </div>
          <Separator orientation="vertical" />
          <Button
            className="mr-1.5 size-[14px]"
            variant="ghost"
            size="icon"
            onClick={() => {
              column.setFilterValue([]);
            }}
          >
            <X />
          </Button>
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={column.columnDef.meta?.displayName ?? column.id} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>

            {/* Render grouped options with custom headers */}
            {groupedOptions.map((group) => {
              const groupValues = group.options.map((opt) => opt.value);
              const allSelected = groupValues.every((value) => selectedValues.has(value));
              const someSelected = groupValues.some((value) => selectedValues.has(value)) && !allSelected;
              const isExpanded = expandedGroups.has(group.groupLabel);

              return (
                <CommandGroup key={group.groupLabel}>
                  {/* Group header with checkbox and chevron */}
                  <div className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={() => {
                        handleToggleGroup(group);
                      }}
                      className="size-4"
                    />
                    <span
                      className="flex-1"
                      onClick={() => {
                        toggleGroupExpansion(group.groupLabel);
                      }}
                    >
                      {group.groupLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        toggleGroupExpansion(group.groupLabel);
                      }}
                      className="flex items-center justify-center text-muted-foreground"
                    >
                      {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </button>
                  </div>

                  {/* Group options - only show when expanded */}
                  {isExpanded &&
                    group.options.map((option) => {
                      const isSelected = selectedValues.has(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          className="ml-6"
                          onSelect={() => {
                            if (isSelected) {
                              selectedValues.delete(option.value);
                            } else {
                              selectedValues.add(option.value);
                            }
                            const filterValues = Array.from(selectedValues);
                            column.setFilterValue(filterValues.length ? filterValues : undefined);
                          }}
                        >
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                              isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible",
                            )}
                          >
                            <Check />
                          </div>
                          {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                          <span>{option.label}</span>
                          {option.uniqueValues && (
                            <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                              {option.uniqueValues}
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                </CommandGroup>
              );
            })}

            {/* Render flat options */}
            {flatOptions.length > 0 && (
              <CommandGroup>
                {flatOptions.map((option) => {
                  const isSelected = selectedValues.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => {
                        if (isSelected) {
                          selectedValues.delete(option.value);
                        } else {
                          selectedValues.add(option.value);
                        }
                        const filterValues = Array.from(selectedValues);
                        column.setFilterValue(filterValues.length ? filterValues : undefined);
                      }}
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <Check />
                      </div>
                      {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                      <span>{option.label}</span>
                      {option.uniqueValues && (
                        <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                          {option.uniqueValues}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      column.setFilterValue(undefined);
                    }}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
